import { WEBGL } from '../../../../vendors/three.examples/WebGL';
import { COLOR_DEFAULT_CLEAR_BG, COLOR_DEFAULT_FOG_START } from '../../../constants/colors';
import {
    SIZE_OPTIONS, SIZE_REFS_MAP, SIZES_COMMON
} from '../../../constants/sizes';
import THREE from '../utils/threeClassesAdapter';
import { TwoNullsCameraRig } from '../camera/TwoNullsCameraRig';

const prepareRenderer = (w, h, canvas = null, transparent = false, logDepthBuf = false, antialias = false, color = null) => {
    if (!WEBGL.isWebGLAvailable()) return null;

    const props = {
        alpha: transparent,
        antialias,
        logarithmicDepthBuffer: logDepthBuf
    };

    if (canvas) props.canvas = canvas;

    const renderer = new THREE.WebGL1Renderer(props);

    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(w, h, false);
    renderer.setClearColor(new THREE.Color(color || COLOR_DEFAULT_CLEAR_BG), 1);

    return renderer;
};

const prepareCamera = (params) => {
    /*
     fov — Camera frustum vertical field of view.
     aspect — Camera frustum aspect ratio.
     near — Camera frustum near plane.
     far — Camera frustum far plane.
    * */

    params = params || {};

    const config = {
        // defaults
        fov: 35,
        aspect: 1,
        near: 1,
        far: 2500,
        ...params
    };
    const camera = new THREE.PerspectiveCamera(config.fov, config.aspect, config.near, config.far);

    return {
        camera,
        rig: new TwoNullsCameraRig(camera)
    };
};

class SceneInfrastructure {
    constructor(renderer, canvas, camParams, w, h, clear, transparent, logDepthBuf, antialias, color) {
        this.renderer = renderer || prepareRenderer(w, h, canvas, transparent, logDepthBuf, antialias, color);

        if (!this.renderer) {

            return;
        }

        const scene = new THREE.Scene();
        const { camera, rig } = prepareCamera(camParams);

        scene.fog = new THREE.Fog(color || COLOR_DEFAULT_FOG_START, camera.near + (0.8 * (camera.far - camera.near)), camera.far);

        this.scene = scene;
        this.camera = camera;
        this.rig = rig;
        this.clear = clear;
    }

    render() {
        if (!this.renderer) {
            // eslint-disable-next-line no-console
            console.warn('There is no renderer initialised');
        }

        this.renderer.autoClearColor = this.clear;
        this.renderer.render(this.scene, this.camera);
        this.renderer.autoClearColor = true;
    }

    getViewSize(depth = 0) {
        const fovInRadians = (this.camera.fov * Math.PI) / 180;

        const { camDistance } = SIZES_COMMON;
        const height = Math.abs(
            (camDistance - depth) * Math.tan(fovInRadians / 2) * 2
        );

        return { width: height * this.camera.aspect, height };
    }

    getPixelRatio() {
        if (!this.renderer) {
            // eslint-disable-next-line no-console
            console.warn('There is no renderer initialised');

            return 1;
        }

        return this.renderer.getPixelRatio();
    }

    getTextScale(sourceViewHeight, targetPixelHeight) {
        if (!this.camera) return 1;

        const viewSize = this.getViewSize();

        if (this.camera.aspect > 1) {
            const refSize = SIZE_REFS_MAP[SIZE_OPTIONS.LANDSCAPE];
            const sourcePixelsSize = (refSize.h / viewSize.height) * sourceViewHeight;

            return targetPixelHeight / sourcePixelsSize;
        }

        const refSize = SIZE_REFS_MAP[SIZE_OPTIONS.PORTRAIT];
        const sourcePixelsSize = (refSize.w / viewSize.width) * sourceViewHeight;

        return targetPixelHeight / sourcePixelsSize;
    }
}

export const prepareInfrastructure = (
    renderer, canvas, camParams, w, h, clear, transparent, logDepthBuf, antialias, color
) => new SceneInfrastructure(
    renderer, canvas, camParams, w, h, clear, transparent, logDepthBuf, antialias, color
);
